import { useFpCampaignCoupons } from '@cart-services/hooks/useFpCampaignCoupons'
import { useFpCampaignCouponService } from '@cart-services/hooks/useFpCampaignCouponService'
import { useFpCategoryPromotionStore } from '@cart-services/hooks/useFpCategoryPromotionStore'
import { CouponCategories } from '@cart-ui/CouponCategories/CouponCategories'
import { CouponTabValue, useCouponCategories } from '@cart-ui/CouponCategories/useCouponCategories'
import { CouponList, type CouponListProps } from '@cart-ui/CouponList/CouponList'
import { type CountryCode, type ProjectCode } from '@constants'
import { useFastpassCampaignGoogleAnalyticEvent } from '@dni-analysis'
import { useDialog } from '@dni-hooks/useDialog'
import { Button } from '@pets-ui/Button/Button'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { Divider } from '@pets-ui/Divider/Divider'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { Tab } from '@pets-ui/Tab/Tab'
import { TabContext } from '@pets-ui/TabContext/TabContext'
import { TabList } from '@pets-ui/TabList/TabList'
import { TabPanel } from '@pets-ui/TabPanel/TabPanel'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom-v5'
import { DialogCouponManualInput } from './DialogCouponManualInput'

export const DIALOG_COUPON = 'DIALOG_COUPON'

export interface DialogCouponProps {
  projectCode: ProjectCode
  countryCode: CountryCode
  currency: string
  campaignId: number
  campaignCode: string
}

const DialogCouponContent = (props: DialogCouponProps & { isMobile: boolean }) => {
  const { projectCode, countryCode, currency, campaignId, campaignCode, isMobile } = props
  const history = useHistory()
  const { closeDialog } = useDialog(DIALOG_COUPON)
  const dialogContentRef = useRef<HTMLDivElement>(null)
  const { availableMergedCoupons } = useFpCampaignCoupons(campaignId, countryCode, campaignCode)
  const { removeManualInputCouponIds } = useFpCampaignCouponService(campaignId, countryCode, campaignCode)
  const {
    categories,
    selectedCategory,
    setSelectedCategory,
    availableCouponsForList,
    handleChangeCategory,
    handleTabChange,
    tabValue,
  } = useCouponCategories(availableMergedCoupons)
  const { setFocusCouponId } = useFpCategoryPromotionStore()
  const { sendGoToPromotionPageEvent } = useFastpassCampaignGoogleAnalyticEvent({ projectCode, countryCode, campaignId })

  /**
   * Improve UX by scrolling to the top and focus on the first item when the category changes
   */
  useEffect(() => {
    if (categories.length > 1 && categories[0]?.value) {
      setSelectedCategory(categories[0]?.value)
      dialogContentRef.current?.scrollTo({ top: 0 })
    }
  }, [categories, setSelectedCategory])

  const handleManualCouponDelete: CouponListProps['onManualCouponDelete'] = (coupon) => {
    removeManualInputCouponIds([coupon.coupon_id])
  }

  const handleCouponGoShopping: CouponListProps['onGoShopping'] = (coupon) => {
    const couponTabSnakeCase = tabValue.replace(/([A-Z])/g, '_$1').toLowerCase()
    sendGoToPromotionPageEvent({ couponTab: couponTabSnakeCase, couponId: coupon.coupon_id, couponTitle: coupon.display_name })
    history.push(`/campaigns/${campaignCode}?tab=promotion`)
    setFocusCouponId(coupon.coupon_id)

    closeDialog()
  }

  return (
    <TabContext value={tabValue}>
      <TabList variant="fullWidth" onChange={handleTabChange}>
        <Tab value={CouponTabValue.AUTO_COUPON}>優惠活動</Tab>
        <Tab value={CouponTabValue.MANUAL_INPUT}>優惠碼</Tab>
      </TabList>
      <TabPanel value={CouponTabValue.AUTO_COUPON}>
        <CouponCategories
          categories={categories}
          value={selectedCategory}
          onChange={handleChangeCategory}
        />
      </TabPanel>
      <DialogContent backgroundColor={colors.grey100} rootRef={dialogContentRef} height={isMobile ? '100vh' : '65vh'}>
        <VStack gap={4} mt={4}>
          <TabPanel value={CouponTabValue.MANUAL_INPUT}>
            <DialogCouponManualInput
              campaignId={campaignId}
              projectCode={projectCode}
              countryCode={countryCode}
              campaignCode={campaignCode}
            />
          </TabPanel>
          {/* NOTE: 2503 手動輸入的優惠券，含有 giveaways 的優惠券，但目前後端沒有回傳 giveaways，所以先註解 */}
          {/* <Text variant='caption' color={colors.grey600}>手動輸入優惠碼的贈品，僅支援宅配寄送</Text> */}
          <CouponList
            coupons={availableCouponsForList}
            currency={currency}
            onManualCouponDelete={handleManualCouponDelete}
            onGoShopping={handleCouponGoShopping}
          />
          {availableCouponsForList && <Text variant="caption" color={colors.grey500} align="center">- 沒有更多優惠折扣了 -</Text>}
        </VStack>
      </DialogContent>
    </TabContext>
  )
}

export const DialogCoupon = () => {
  const { open, closeDialog, projectCode, countryCode, currency, campaignId, campaignCode } = useDialog<DialogCouponProps>(DIALOG_COUPON)
  const isMobile = useResponsive('mobile')
  const { sendLeaveCouponEvent } = useFastpassCampaignGoogleAnalyticEvent({ projectCode, countryCode, campaignId })

  const handleCouponDialogClose = () => {
    closeDialog()
    sendLeaveCouponEvent()
  }

  return (
    <ResponsiveDialog open={open} onClose={handleCouponDialogClose} fullWidth maxHeight="calc(90% - 64px)">
      <DialogHeader title="適用優惠折扣" onClose={handleCouponDialogClose} />
      <Divider />
      <DialogCouponContent
        projectCode={projectCode}
        countryCode={countryCode}
        currency={currency}
        campaignId={campaignId}
        campaignCode={campaignCode}
        isMobile={isMobile}
      />
      <DialogActions>
        <Button onClick={handleCouponDialogClose} fullWidth={isMobile}>
          確認
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}
